import { useState, useEffect } from "react";
import { shareLink } from "@/lib/pageMeta";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Zap, Calendar, Users, Target, Trash2, X, Loader2, UserCheck, Stethoscope, Pencil, Check, Link2, QrCode, Copy, RefreshCw
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const CorporatePrograms = () => {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [dietitians, setDietitians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [assigningDietitian, setAssigningDietitian] = useState(null);
  const [inviteMode, setInviteMode] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [selectedDtId, setSelectedDtId] = useState("");
  const [form, setForm] = useState({
    name: "", description: "", 
    challenge_target_label: "Sugar", challenge_target_value: 25, challenge_target_unit: "g",
    start_date: "", end_date: "", dietitian_id: ""
  });

  const fetchPrograms = async () => {
    try {
      const [progRes, dtRes] = await Promise.all([
        fetch(`${API}/corporate/programs`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/corporate/available-dietitians`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (progRes.ok) { const d = await progRes.json(); setPrograms(d.programs || []); }
      if (dtRes.ok) { const d = await dtRes.json(); setDietitians(d.dietitians || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPrograms(); }, [token]); // eslint-disable-line

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error("Name, start date, and end date are required"); return;
    }
    setCreating(true);
    try {
      const body = { ...form };
      if (!body.dietitian_id) delete body.dietitian_id;
      const res = await fetch(`${API}/corporate/programs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success("Program created!");
        setShowCreate(false);
        setForm({ name: "", description: "", challenge_target_label: "Sugar", challenge_target_value: 25, challenge_target_unit: "g", start_date: "", end_date: "", dietitian_id: "" });
        fetchPrograms();
      } else { const err = await res.json(); toast.error(err.detail || "Failed to create program"); }
    } catch (err) { toast.error("Error creating program"); }
    finally { setCreating(false); }
  };

  const startEdit = (prog) => {
    setEditingId(prog.program_id);
    setEditForm({
      name: prog.name, description: prog.description || "",
      challenge_target_label: prog.challenge_target_label || "Sugar",
      challenge_target_value: prog.challenge_target_value || prog.sugar_target_grams || 25,
      challenge_target_unit: prog.challenge_target_unit || "g",
      start_date: prog.start_date, end_date: prog.end_date
    });
  };

  const handleUpdate = async (programId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/corporate/programs/${programId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (res.ok) { toast.success("Program updated"); setEditingId(null); fetchPrograms(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update"); }
    } catch (err) { toast.error("Error updating program"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (programId, name) => {
    if (!window.confirm(`Delete "${name}" and all its enrollments? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/corporate/programs/${programId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Program deleted"); fetchPrograms(); }
    } catch (err) { toast.error("Error deleting program"); }
  };

  const handleAssignDietitian = async (programId) => {
    try {
      const body = inviteMode
        ? { invite_email: inviteEmail.trim(), invite_name: inviteName.trim() }
        : { dietitian_id: selectedDtId };
      if (!body.dietitian_id && !body.invite_email) {
        toast.error("Select a dietitian or enter an email to invite"); return;
      }
      const res = await fetch(`${API}/corporate/programs/${programId}/assign-dietitian`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Dietitian ${data.dietitian?.name || ""} assigned!`);
        setAssigningDietitian(null); setInviteMode(false); setInviteEmail(""); setInviteName(""); setSelectedDtId("");
        fetchPrograms();
      } else { const err = await res.json(); toast.error(err.detail || "Failed to assign"); }
    } catch (err) { toast.error("Error assigning dietitian"); }
  };

  const handleRemoveDietitian = async (programId, dietitianId, name) => {
    if (!window.confirm(`Remove ${name} from this program?`)) return;
    try {
      const res = await fetch(`${API}/corporate/programs/${programId}/dietitians/${dietitianId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`${name} removed`);
        fetchPrograms();
      } else { const err = await res.json(); toast.error(err.detail || "Failed"); }
    } catch { toast.error("Error removing dietitian"); }
  };

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    upcoming: "bg-blue-100 text-blue-700",
    completed: "bg-gray-100 text-gray-700"
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-secondary">Programs & Challenges</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Create and manage wellness challenges for your team</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl shrink-0 text-xs sm:text-sm" data-testid="create-program-btn">
          {showCreate ? <X className="w-4 h-4 sm:mr-2" /> : <Plus className="w-4 h-4 sm:mr-2" />}
          <span className="hidden sm:inline">{showCreate ? "Cancel" : "New Program"}</span>
        </Button>
      </div>

      {showCreate && (
        <Card className="rounded-2xl border-emerald-200 p-3 sm:p-5 space-y-3 sm:space-y-4" data-testid="create-program-form">
          <h3 className="font-bold text-secondary text-sm sm:text-base">New Challenge</h3>
          <Input placeholder="Challenge name (e.g. 21-Day Sugar Detox)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl text-sm" data-testid="program-name-input" />
          <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl text-sm" rows={2} />
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <div className="sm:col-span-3">
              <label className="text-xs text-muted-foreground mb-1 block">Daily Target</label>
              <div className="flex gap-2">
                <Input placeholder="e.g. Sugar, Sodium" value={form.challenge_target_label}
                  onChange={(e) => setForm({ ...form, challenge_target_label: e.target.value })}
                  className="rounded-xl flex-1 text-sm" data-testid="program-target-label" />
                <Input type="number" value={form.challenge_target_value}
                  onChange={(e) => setForm({ ...form, challenge_target_value: parseInt(e.target.value) || 0 })}
                  className="rounded-xl w-16 sm:w-20 text-sm" data-testid="program-target-value" />
                <Select value={form.challenge_target_unit} onValueChange={(v) => setForm({ ...form, challenge_target_unit: v })}>
                  <SelectTrigger className="rounded-xl w-16 sm:w-20" data-testid="program-target-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="mg">mg</SelectItem>
                    <SelectItem value="kcal">kcal</SelectItem>
                    <SelectItem value="steps">steps</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl" data-testid="program-start-date" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl" data-testid="program-end-date" />
            </div>
          </div>
          {dietitians.length > 0 && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Assign Wellness Coach (optional)</label>
              <Select value={form.dietitian_id} onValueChange={(v) => setForm({ ...form, dietitian_id: v })}>
                <SelectTrigger className="rounded-xl" data-testid="program-dietitian-select"><SelectValue placeholder="Select a physician/coach" /></SelectTrigger>
                <SelectContent>
                  {dietitians.map((d) => (
                    <SelectItem key={d.user_id} value={d.user_id}>{d.name} ({d.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="submit-program-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Create Challenge
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="space-y-4">{[1, 2].map((i) => <div key={i} className="animate-pulse h-32 bg-white rounded-2xl" />)}</div>
      ) : programs.length === 0 ? (
        <Card className="rounded-2xl p-8 text-center border-emerald-100">
          <Zap className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No programs yet. Create your first wellness challenge!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {programs.map((prog) => (
            <Card key={prog.program_id} className="rounded-2xl border-emerald-100/50 overflow-hidden" data-testid={`program-${prog.program_id}`}>
              <CardContent className="p-5">
                {editingId === prog.program_id ? (
                  /* ===== EDIT MODE ===== */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-secondary text-sm">Edit Program</h3>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                    </div>
                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-xl" placeholder="Program name" data-testid="edit-program-name" />
                    <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="rounded-xl" rows={2} placeholder="Description" />
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className="col-span-2 sm:col-span-3">
                        <label className="text-xs text-muted-foreground mb-1 block">Daily Target</label>
                        <div className="flex gap-2">
                          <Input placeholder="e.g. Sugar, Sodium" value={editForm.challenge_target_label}
                            onChange={(e) => setEditForm({ ...editForm, challenge_target_label: e.target.value })}
                            className="rounded-xl flex-1" />
                          <Input type="number" value={editForm.challenge_target_value}
                            onChange={(e) => setEditForm({ ...editForm, challenge_target_value: parseInt(e.target.value) || 0 })}
                            className="rounded-xl w-20" />
                          <Select value={editForm.challenge_target_unit} onValueChange={(v) => setEditForm({ ...editForm, challenge_target_unit: v })}>
                            <SelectTrigger className="rounded-xl w-20"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">g</SelectItem>
                              <SelectItem value="mg">mg</SelectItem>
                              <SelectItem value="kcal">kcal</SelectItem>
                              <SelectItem value="steps">steps</SelectItem>
                              <SelectItem value="ml">ml</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                        <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="rounded-xl" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                        <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="rounded-xl" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => handleUpdate(prog.program_id)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="save-program-edit">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-xl">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  /* ===== VIEW MODE ===== */
                  <>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-secondary text-lg">{prog.name}</h3>
                          <Badge className={`text-xs ${statusColors[prog.status] || statusColors.upcoming}`}>{prog.status}</Badge>
                        </div>
                        {prog.description && <p className="text-sm text-muted-foreground">{prog.description}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(prog)} className="text-muted-foreground hover:text-emerald-600" data-testid={`edit-program-${prog.program_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(prog.program_id, prog.name)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-program-${prog.program_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:gap-4 mt-3 sm:mt-4 text-xs sm:text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground"><Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{prog.start_date} — {prog.end_date}</div>
                      <div className="flex items-center gap-1.5 text-muted-foreground"><Target className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{prog.challenge_target_value || prog.sugar_target_grams || 25}{prog.challenge_target_unit || "g"} {prog.challenge_target_label || "Sugar"}/day</div>
                      <div className="flex items-center gap-1.5 text-muted-foreground"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{prog.participant_count} participants</div>
                      <div className="flex items-center gap-1.5 text-muted-foreground"><Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{prog.duration_days} days</div>
                    </div>

                    {/* Dietitian Assignment — Multi-dietitian */}
                    <div className="mt-4 pt-3 border-t border-emerald-100/50">
                      {(prog.dietitians?.length > 0) ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Stethoscope className="w-4 h-4 text-emerald-600" />
                              <span>Wellness Coaches ({prog.dietitians.length})</span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setAssigningDietitian(prog.program_id)} className="text-xs text-emerald-600 hover:text-emerald-700">
                              <UserCheck className="w-3 h-3 mr-1" /> Add More
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {prog.dietitians.map((dt) => (
                              <div key={dt.user_id} className="flex items-center gap-1.5 bg-emerald-50 rounded-lg px-2.5 py-1.5 text-xs">
                                <span className="font-medium text-secondary">{dt.name}</span>
                                <span className="text-muted-foreground">({dt.email})</span>
                                <button onClick={() => handleRemoveDietitian(prog.program_id, dt.user_id, dt.name)}
                                  className="ml-1 text-muted-foreground hover:text-red-500 transition-colors"
                                  data-testid={`remove-dt-${dt.user_id}`}>
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setAssigningDietitian(prog.program_id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 rounded-xl" data-testid={`assign-dietitian-${prog.program_id}`}>
                          <UserCheck className="w-4 h-4 mr-2" /> Assign Wellness Coach
                        </Button>
                      )}

                      {assigningDietitian === prog.program_id && (
                        <div className="mt-3 p-3 bg-emerald-50/50 rounded-xl space-y-3" data-testid="assign-dietitian-form">
                          <div className="flex gap-2">
                            <button onClick={() => setInviteMode(false)} className={`text-xs px-3 py-1 rounded-full ${!inviteMode ? "bg-emerald-600 text-white" : "bg-white text-muted-foreground border"}`}>Select Existing</button>
                            <button onClick={() => setInviteMode(true)} className={`text-xs px-3 py-1 rounded-full ${inviteMode ? "bg-emerald-600 text-white" : "bg-white text-muted-foreground border"}`}>Invite New</button>
                          </div>
                          {!inviteMode ? (
                            <Select value={selectedDtId} onValueChange={setSelectedDtId}>
                              <SelectTrigger className="rounded-xl text-sm"><SelectValue placeholder="Select physician" /></SelectTrigger>
                              <SelectContent>
                                {dietitians.map((d) => (
                                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} ({d.email})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="space-y-2">
                              <Input placeholder="Coach name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} className="rounded-xl text-sm" />
                              <Input placeholder="Coach email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="rounded-xl text-sm" data-testid="invite-dietitian-email" />
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleAssignDietitian(prog.program_id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs">
                              <UserCheck className="w-3 h-3 mr-1" /> Assign
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setAssigningDietitian(null); setInviteMode(false); }} className="text-xs">Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Self-Enrollment Link + QR Code */}
                    {prog.invite_code ? (
                      <div className="mt-4 pt-3 border-t border-orange-100/50">
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
                          <Link2 className="w-4 h-4 text-primary" />
                          <span>Employee Self-Enrollment</span>
                        </div>
                        <div className="bg-orange-50/50 rounded-xl p-3 sm:p-4 space-y-3">
                          {/* Invite Link */}
                          <div className="flex gap-1.5 sm:gap-2">
                            <Input readOnly value={shareLink(`/join/${prog.invite_code}`)}
                              className="rounded-xl text-[10px] sm:text-xs bg-white flex-1 font-mono"
                              data-testid={`invite-link-${prog.program_id}`} />
                            <Button size="sm" variant="outline" className="rounded-xl shrink-0"
                              data-testid={`copy-link-${prog.program_id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(shareLink(`/join/${prog.invite_code}`));
                                toast.success("Link copied!");
                              }}>
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-xl shrink-0 text-muted-foreground"
                              data-testid={`regen-code-${prog.program_id}`}
                              onClick={async () => {
                                const res = await fetch(`${API}/corporate/programs/${prog.program_id}/regenerate-invite`, {
                                  method: "POST", headers: { Authorization: `Bearer ${token}` }
                                });
                                if (res.ok) { toast.success("New code generated"); fetchPrograms(); }
                              }}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {/* QR Code */}
                          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                            <div className="bg-white p-2 sm:p-3 rounded-xl border border-orange-100 inline-block"
                              data-testid={`qr-code-${prog.program_id}`}>
                              <QRCodeSVG value={`${window.location.origin}/join/${prog.invite_code}`} size={100}
                                fgColor="#1F2933" bgColor="#FFFFFF" level="M" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <p className="text-xs text-muted-foreground">
                                Share this link or print the QR code for your office. Employees scan to sign up themselves — no manual onboarding needed.
                              </p>
                              <Button size="sm" variant="outline" className="rounded-xl text-xs"
                                onClick={() => {
                                  const svg = document.querySelector(`[data-testid="qr-code-${prog.program_id}"] svg`);
                                  if (!svg) return;
                                  const data = new XMLSerializer().serializeToString(svg);
                                  const blob = new Blob([data], { type: "image/svg+xml" });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url; a.download = `${prog.name}-QR-Code.svg`; a.click();
                                  URL.revokeObjectURL(url);
                                  toast.success("QR code downloaded!");
                                }}>
                                <QrCode className="w-3.5 h-3.5 mr-1.5" /> Download QR Code
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 pt-3 border-t border-orange-100/50">
                        <Button variant="outline" size="sm" className="text-primary border-primary/30 hover:bg-orange-50 rounded-xl"
                          data-testid={`generate-invite-${prog.program_id}`}
                          onClick={async () => {
                            const res = await fetch(`${API}/corporate/programs/${prog.program_id}/regenerate-invite`, {
                              method: "POST", headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) { toast.success("Invite link generated!"); fetchPrograms(); }
                            else { toast.error("Failed to generate invite link"); }
                          }}>
                          <Link2 className="w-4 h-4 mr-2" /> Generate Invite Link & QR Code
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CorporatePrograms;
